/**
 * Feature Flag Utility
 * Centralized feature flag management for the React application
 */

import { fetchBackendFeatureFlags } from './backendFeatureFlags';

/**
 * Check if a feature flag is enabled
 * @param {string} flagName - The name of the feature flag (without REACT_APP_ prefix)
 * @returns {boolean} - Whether the feature is enabled
 */
export const isFeatureEnabled = (flagName) => {
  // Due to Webpack's DefinePlugin behavior, we need to access environment variables directly
  // rather than using dynamic template literals, as Webpack replaces them at build time
  switch (flagName) {
    case 'BULK_SHIPMENTS':
      return process.env.REACT_APP_ENABLE_BULK_SHIPMENTS === 'true';
    case 'PAYPAL':
      return process.env.REACT_APP_ENABLE_PAYPAL === 'true';
    case 'ORDER_CANCELLATION':
      return process.env.REACT_APP_ENABLE_ORDER_CANCELLATION === 'true';
    case 'BLOGS':
      return process.env.REACT_APP_ENABLE_BLOGS === 'true';
    case 'ECOMMERCE_INTEGRATIONS':
      return process.env.REACT_APP_ENABLE_ECOMMERCE_INTEGRATIONS === 'true';
    case 'SELLER_TOOLS':
      return process.env.REACT_APP_ENABLE_SELLER_TOOLS === 'true';
    case 'SHIPPING_OPTIONS':
      return process.env.REACT_APP_ENABLE_SHIPPING_OPTIONS === 'true';
    case 'GTM':
      return process.env.REACT_APP_ENABLE_GTM === 'true';
    default:
      console.warn(`[Feature Flags] Unknown feature flag: ${flagName}`);
      return false;
  }
};

/**
 * Get feature flag value as string
 * @param {string} flagName - The name of the feature flag (without REACT_APP_ prefix)
 * @returns {string|null} - The feature flag value or null if not set
 */
export const getFeatureFlag = (flagName) => {
  const envVarName = `REACT_APP_ENABLE_${flagName.toUpperCase()}`;
  return process.env[envVarName] || null;
};

/**
 * Feature flag constants for easier usage
 */
export const FEATURES = {
  BULK_SHIPMENTS: 'BULK_SHIPMENTS',
  PAYPAL: 'PAYPAL',
  ORDER_CANCELLATION: 'ORDER_CANCELLATION',
  BLOGS: 'BLOGS',
  ECOMMERCE_INTEGRATIONS: 'ECOMMERCE_INTEGRATIONS',
  SELLER_TOOLS: 'SELLER_TOOLS',
  SHIPPING_OPTIONS: 'SHIPPING_OPTIONS'
};

/**
 * Helper function to get all enabled features for debugging
 * @returns {object} - Object with all feature flags and their states
 */
export const getAllFeatureFlags = () => {
  const flags = {};
  
  Object.values(FEATURES).forEach(feature => {
    flags[feature] = isFeatureEnabled(feature);
  });
  
  // Add any other React environment variables that start with REACT_APP_ENABLE_
  Object.keys(process.env).forEach(key => {
    if (key.startsWith('REACT_APP_ENABLE_')) {
      const featureName = key.replace('REACT_APP_ENABLE_', '');
      if (!flags[featureName]) {
        flags[featureName] = isFeatureEnabled(featureName);
      }
    }
  });
  
  return flags;
};

/**
 * Validate that frontend and backend feature flags are in sync
 * @returns {Promise<object>} - Validation results including mismatches
 */
export const validateFeatureFlagSync = async () => {
  try {
    const frontendFlags = getAllFeatureFlags();
    const backendFlags = await fetchBackendFeatureFlags();
    
    const mismatches = [];
    const validationResults = {
      frontendFlags,
      backendFlags,
      mismatches,
      isInSync: true
    };
    
    // Check for mismatches in known features
    const featureMappings = {
      'BULK_SHIPMENTS': 'bulkShipments',
      'BLOGS': 'blogs',
      'ECOMMERCE_INTEGRATIONS': 'ecommerceIntegrations'
    };
    
    Object.entries(featureMappings).forEach(([frontendKey, backendKey]) => {
      const frontendValue = frontendFlags[frontendKey];
      const backendValue = backendFlags[backendKey];
      
      if (frontendValue !== backendValue) {
        mismatches.push({
          feature: frontendKey,
          frontend: frontendValue,
          backend: backendValue
        });
        validationResults.isInSync = false;
      }
    });
    
    return validationResults;
    
  } catch (error) {
    console.error('[Feature Flags] Error validating sync:', error);
    return {
      frontendFlags: getAllFeatureFlags(),
      backendFlags: {},
      mismatches: [],
      isInSync: false,
      error: error.message
    };
  }
};

/**
 * Log all feature flags for debugging (development only)
 */
export const logFeatureFlags = async () => {
  if (process.env.NODE_ENV === 'development') {
    const frontendFlags = getAllFeatureFlags();
    console.log('🎛️ Frontend Feature Flags:', frontendFlags);
    
    try {
      const validation = await validateFeatureFlagSync();
      console.log('🔄 Feature Flag Sync Status:', validation);
      
      if (!validation.isInSync) {
        console.warn('⚠️ Feature flag mismatches detected:', validation.mismatches);
      }
    } catch (error) {
      console.error('❌ Could not validate feature flag sync:', error);
    }
  }
};
